/**
 * Box Component
 * The most fundamental building block with style props support
 */

import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { transformStyleProps, splitProps, stylePropsKeys } from '../system/style-props';

/**
 * Base styled component that filters style props from DOM
 */
const StyledBox = styled('div', {
  shouldForwardProp: (prop) => {
    if (stylePropsKeys.includes(prop)) return false;
    if (['as', 'sx', '__css'].includes(prop)) return false;
    return true;
  },
})(({ theme, ...props }) => {
  const styles = [];

  // Apply __css internal styles
  if (props.__css) {
    const cssResult = typeof props.__css === 'function'
      ? props.__css({ theme, ...props })
      : props.__css;
    styles.push(transformStyleProps(cssResult, theme));
  }

  // Apply style props
  const [styleProps] = splitProps(props);
  styles.push(transformStyleProps(styleProps, theme));

  // Apply sx prop (user overrides)
  if (props.sx) {
    const sxResult = typeof props.sx === 'function'
      ? props.sx({ theme, ...props })
      : props.sx;
    styles.push(transformStyleProps(sxResult, theme));
  }

  return styles;
});

/**
 * Box Component
 * The most abstract component on top of which all other components are built.
 * 
 * @example
 * <Box bg="brand.500" p={4} borderRadius="lg">
 *   Content
 * </Box>
 */
export const Box = forwardRef(({ as, children, ...props }, ref) => {
  return (
    <StyledBox as={as} ref={ref} {...props}>
      {children}
    </StyledBox>
  );
});

Box.displayName = 'Box';

export default Box;
